/**
 * NodeTextEditor — inline plain-text rewrite for one node (S6.2 F1, D-F1-2=A).
 *
 * A node-scoped textarea, not a rich-text editor: saving collapses the node
 * to a single text run. Ctrl/Cmd+Enter saves, Esc cancels.
 */
import { useEffect, useRef, useState } from 'react';

interface NodeTextEditorProps {
  initialText: string;
  busy: boolean;
  onSave: (text: string) => void;
  onCancel: () => void;
}

export function NodeTextEditor({
  initialText,
  busy,
  onSave,
  onCancel,
}: NodeTextEditorProps): React.JSX.Element {
  const [value, setValue] = useState(initialText);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const dirty = value !== initialText;

  return (
    <div className="flex flex-col gap-2">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            if (!busy) onSave(value);
          }
        }}
        rows={Math.min(8, Math.max(2, value.split('\n').length))}
        className="w-full resize-y rounded-md border border-brand-500 bg-ink-0 px-3 py-2 font-serif text-md leading-relaxed text-ink-900 outline-none focus:ring-2 focus:ring-brand-200"
        aria-label="编辑节点文字"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onSave(value)}
          disabled={busy || !dirty}
          className="inline-flex h-btn-sm items-center rounded-sm bg-brand-600 px-3 text-xs font-medium text-ink-0 hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          保存
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="inline-flex h-btn-sm items-center rounded-sm border border-ink-200 bg-ink-50 px-3 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900 disabled:opacity-40"
        >
          取消
        </button>
        <span className="text-xs text-ink-400">⌘/Ctrl+Enter 保存 · Esc 取消</span>
      </div>
    </div>
  );
}

export default NodeTextEditor;
